import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  Paper,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

const HISTORY_PAGE_SIZE = 12;

type BulkMailHistoryRow = {
  id: string;
  createdAt: string | Date;
  subject: string;
  recipientEmails: string[];
  queuedCount: number;
  failedCount: number;
  sentBy: { id: string; email: string; name: string };
  sharedProfiles: { companyId: string; legalName: string; gtid: string | null }[];
};

export default function AdminBulkMailHistoryPage() {
  const navigate = useNavigate();
  const [historyRows, setHistoryRows] = useState<BulkMailHistoryRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPagination, setHistoryPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: HISTORY_PAGE_SIZE,
  });
  const previousHistoryParamsRef = useRef<string>("");

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await trpc.admin.bulkMailSendHistory.query({
        page: historyPagination.pageIndex + 1,
        limit: historyPagination.pageSize,
      });
      const rows = res.rows as BulkMailHistoryRow[];
      setHistoryRows(
        rows.map((r) => ({
          ...r,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date(String(r.createdAt)),
        }))
      );
      setHistoryTotal(res.meta.total);
    } catch (err) {
      handleTrpcError(err);
      setHistoryRows([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPagination.pageIndex, historyPagination.pageSize]);

  useEffect(() => {
    const key = JSON.stringify({
      page: historyPagination.pageIndex,
      pageSize: historyPagination.pageSize,
    });
    if (key === previousHistoryParamsRef.current) {
      return;
    }
    previousHistoryParamsRef.current = key;
    void fetchHistory();
  }, [fetchHistory, historyPagination.pageIndex, historyPagination.pageSize]);

  const historyColumns = useMemo<MRT_ColumnDef<BulkMailHistoryRow>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "Sent at",
        size: 168,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | Date>();
          const d = v instanceof Date ? v : new Date(v);
          return <Text size="sm">{d.toLocaleString()}</Text>;
        },
      },
      {
        id: "sentBy",
        header: "Sent by",
        size: 200,
        Cell: ({ row }) => (
          <Text size="sm">
            {row.original.sentBy.email}
            <Text span c="dimmed" size="xs" display="block">
              {row.original.sentBy.name}
            </Text>
          </Text>
        ),
      },
      {
        accessorKey: "subject",
        header: "Subject",
        size: 200,
        Cell: ({ row }) => (
          <Tooltip label={row.original.subject} multiline maw={400} withArrow>
            <Text size="sm" lineClamp={4} style={{ wordBreak: "break-word" }}>
              {row.original.subject}
            </Text>
          </Tooltip>
        ),
      },
      {
        id: "recipients",
        header: "Recipients",
        size: 260,
        Cell: ({ row }) => {
          const emails = row.original.recipientEmails;
          return (
            <Flex direction="column" gap={6} align="stretch">
              {emails.map((email, i) => (
                <Text key={`${email}-${i}`} size="sm" style={{ wordBreak: "break-all", lineHeight: 1.45 }}>
                  {email}
                </Text>
              ))}
            </Flex>
          );
        },
      },
      {
        id: "profiles",
        header: "Profiles shared",
        size: 300,
        Cell: ({ row }) => {
          const ps = row.original.sharedProfiles;
          if (ps.length === 0) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Flex direction="column" gap={6} align="stretch">
              {ps.map((p) => (
                <Text key={p.companyId} size="sm" style={{ wordBreak: "break-word", lineHeight: 1.45 }}>
                  {p.legalName} ({p.gtid ?? p.companyId})
                </Text>
              ))}
            </Flex>
          );
        },
      },
      {
        id: "result",
        header: "Queue",
        size: 120,
        Cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            <Badge size="sm" color="green" variant="light">
              {row.original.queuedCount} ok
            </Badge>
            {row.original.failedCount > 0 ? (
              <Badge size="sm" color="orange" variant="light">
                {row.original.failedCount} fail
              </Badge>
            ) : null}
          </Group>
        ),
      },
    ],
    []
  );

  return (
    <Flex direction="column" p="lg" gap="lg" mih="100%">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>Bulk mail — send history</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Each row is one send: who ran it, when, subject, every recipient email, and which company profiles were
            included (for anonymous discover links).
          </Text>
        </div>
        <Button
          variant="default"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() => navigate("/admin/bulk-mail")}
        >
          Back to compose
        </Button>
      </Group>

      <Paper p="md" withBorder>
        <MantineReactTable
          columns={historyColumns}
          data={historyRows}
          getRowId={(row) => row.id}
          state={{
            isLoading: historyLoading,
            pagination: historyPagination,
          }}
          onPaginationChange={setHistoryPagination}
          manualPagination
          rowCount={historyTotal}
          enableGlobalFilter={false}
          enableColumnFilters={false}
          mantineTableContainerProps={{ style: { minHeight: 360 } }}
        />
        {historyLoading && historyRows.length === 0 && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
      </Paper>
    </Flex>
  );
}
